<script setup lang="ts">
import type { PageState } from 'primevue/paginator';
import { debounce } from 'lodash-es';
import {
  ref,
} from 'vue';

import VFormNumberInput
  from '@modules/forms/components/inputs/VFormNumberInput/VFormNumberInput.vue';
import { FORM_INPUT_DEBOUNCE_DEFAULT_MS } from '@modules/forms/constants';
import { QA_TABLES, TRANSLATION_GROUP_TABLE } from '@modules/tables/constants';
import { isNumber } from '@utils/guards';

import type { ITablePaginationEmits, ITablePaginationProps } from './types';

const props = defineProps<ITablePaginationProps>();
const emit = defineEmits<ITablePaginationEmits>();

function isValidNumber(value: number): boolean {
  return value >= 1 && value <= props.totalPages;
}

const isPageInputValid = ref(true);

function pageUpdate(val: PageState): void {
  emit('pagination-update', val.page + 1);
}

function handleChangePage(val?: number | null): void {
  if (isNumber(val) && isValidNumber(val)) {
    isPageInputValid.value = true;
    emit('pagination-update', val);
  } else {
    isPageInputValid.value = false;
  }
}

const debouncedUpdate = debounce(handleChangePage, FORM_INPUT_DEBOUNCE_DEFAULT_MS);
</script>

<template>
  <div v-data-qa-auto-id="'VTablePagination'"
    v-if="!dataIsEmpty && totalPages > 1"
    v-data-test="{ el: QA_TABLES.pagination }"
    class="flex items-center"
  >
    <VFormNumberInput
      v-data-test="{ el: QA_TABLES.paginationPageInput }"
      name="page"
      hide-errors
      :min="1"
      :max="totalPages"
      :readonly="disabled"
      :format="false"
      :value="page"
      :is-invalid="!isPageInputValid"
      :max-fraction-digits="0"
      :readonly-style="false"
      is-small
      disable-buttons
      class="mr-2 !w-16"
      @input="debouncedUpdate"
      @blur="handleChangePage"
    />
    <PPaginator
      template="CurrentPageReport PrevPageLink NextPageLink"
      :current-page-report-template="`{first}–{last} ${
        $getTranslation(TRANSLATION_GROUP_TABLE, 'PAGINATION_FROM')
      } {totalRecords}`"
      :rows="rowsOnPage"
      :total-records="totalEntries"
      :first="startRowNum - 1"
      :pt="{
        root: { class: 'p-input-filled !p-0' },
        current: { class: 'cursor-default' },
        nextPageButton: { 'class': `${disabled ? 'p-disabled' : ''}`, 'data-test-el': QA_TABLES.paginationNext },
        previousPageButton: { 'class': `${disabled ? 'p-disabled' : ''}`, 'data-test-el': QA_TABLES.paginationPrev },
        previousPageIcon: { class: 'w-6 h-6' },
        nextPageIcon: { class: 'w-6 h-6' },
      }"
      @page="pageUpdate"
    />
  </div>
</template>
